import { useState, useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccountFlow, useCurrencySymbol } from '@shared/hooks';
import { AmountUtils, cn, CurrencyUtils, PaletteUtils } from '@shared/utils';
import { type AccountResponse, AccountType } from '@shared/api';
import { ErrorMessage, Icon, Modal, NoData, Text } from '@shared/widgets';
import DollarIcon from '@assets/icons/dollar.svg';
import SwapIcon from '@assets/icons/swap.svg';

interface SelectAccountProps {
  className?: string;
  loading?: boolean;
  label?: ReactNode;
  type?: AccountType;
  accounts: AccountResponse[];
  error?: ReactNode;
  value?: AccountResponse | null;
  onChange?: (value: AccountResponse) => void;
}

export const SelectAccount = (props: SelectAccountProps) => {
  const [open, setOpen] = useState<boolean>(false);

  const { t } = useTranslation();

  const selectHandler = (value: AccountResponse) => {
    props.onChange?.(value);
    setOpen(false);
  };

  const title = useMemo(() => props.label ?? t('placeholders.selectAccount'), [props.label, t]);
  const accountsPaymentList = useMemo(
    () => (props.type ? props.accounts.filter((account) => account.type === props.type) : props.accounts),
    [props.type, props.accounts],
  );
  return (
    <>
      <div className={cn('flex flex-col gap-3', props.className)}>
        <Text className={cn(PaletteUtils.isVcfcPalette && 'dark:text-white/50')}>{title}</Text>
        <div>
          <AccountListItem loading={props.loading} account={props.value} isShowEndIcon onClick={() => setOpen(true)} />
          <ErrorMessage text={props.error} />
        </div>
      </div>
      <Modal isOpen={open} onClose={() => setOpen(false)} title={title}>
        {accountsPaymentList.length === 0 ? (
          <NoData />
        ) : (
          <div className='flex flex-col gap-2'>
            {accountsPaymentList.map((account) => (
              <AccountListItem
                key={account.id}
                className={cn('w-[456px]', PaletteUtils.isInsigniaPalette && 'dark:bg-slate', 'dark:hover:bg-slate/70')}
                account={account}
                onClick={() => selectHandler(account)}
              />
            ))}
          </div>
        )}
      </Modal>
    </>
  );
};

interface AccountListItemProps {
  className?: string;
  titleClassName?: string;
  loading?: boolean;
  account?: AccountResponse | null;
  isShowEndIcon?: boolean;
  onClick: () => void;
}

const AccountListItem = (props: AccountListItemProps) => {
  const { t } = useTranslation();

  const accountFlow = useAccountFlow(props.account?.id);
  const currencySymbol = useCurrencySymbol(props.account?.currency);
  return (
    <div
      className={cn(
        'flex cursor-pointer flex-row p-3 transition-colors duration-200',
        PaletteUtils.isInsigniaPalette && ['bg-ice font-[Prata] dark:bg-eerie-black', 'hover:bg-ghost-white dark:hover:bg-transport-black'],
        PaletteUtils.isVcfcPalette && ['bg-ghost-white dark:bg-light/5', 'hover:bg-gainsborough dark:hover:bg-light/20'],
        props.loading && 'pointer-events-none',
        props.className,
      )}
      onClick={() => !props.loading && props.onClick()}
    >
      <div
        className={cn(
          'flex h-[42px] w-[68px] items-center justify-center rounded-[5px] bg-soft-black text-2xl',
          PaletteUtils.isInsigniaPalette && 'text-primary',
          PaletteUtils.isVcfcPalette && 'text-white',
        )}
      >
        {currencySymbol ? currencySymbol : <Icon icon={<DollarIcon />} color='inherit' />}
      </div>
      <div className='my-auto ml-3 flex flex-col justify-between'>
        {props.loading && <Text>{t('select.loading', { ns: 'common' })}...</Text>}
        {!props.loading && (
          <>
            {props.account ? (
              <>
                <Text size='xl' className={props.titleClassName}>
                  {accountFlow?.viewAvailableBalance
                    ? AmountUtils.transformToAmountWithCurrency(props.account.availableBalance, props.account.currency)
                    : '****'}
                </Text>
                <Text weight='semibold' size='xs' color='secondary'>
                  {props.account.productName} {props.account.accountNumber}
                </Text>
              </>
            ) : (
              <Text>{t('placeholders.selectAccount')}</Text>
            )}
          </>
        )}
      </div>
      {props.isShowEndIcon && (
        <div className={cn('ml-auto flex items-center gap-3', PaletteUtils.isVcfcPalette && 'ml-6')}>
          {props.account && (
            <div
              className={cn(
                'flex h-7 min-w-16 items-center justify-center gap-2 rounded border border-primary p-2',
                PaletteUtils.isVcfcPalette && 'h-[34px] bg-white dark:border-white dark:bg-light/5',
              )}
            >
              <Text weight='semibold' size='xs' color='primary'>
                {CurrencyUtils.getSymbolByCode(props.account?.currency)}
              </Text>
              <Text uppercase weight='semibold' size='xs' color='primary'>
                {props.account.mainAccId === null ? t('accountType.type.main') : t('accountType.type.sub')}
              </Text>
            </div>
          )}
          <Icon className='rotate-90' icon={<SwapIcon width='24' height='24' />} />
        </div>
      )}
    </div>
  );
};
